import { useState, useEffect, useRef, useCallback } from 'react';
import { useTenantStore, Tenant } from '@/store/tenant.store';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth.store';
import { useMenuKeyboardNav } from '@/hooks/useMenuKeyboardNav';
import { api } from '@/services/api';

import FaIcon from '@/components/common/FaIcon';
import { BuildingIcon, CheckIcon } from '../icons';

function ChevronIcon({ className, isOpen }: { className?: string; isOpen: boolean }) {
  return (
    <FaIcon icon="chevron-down" className={`${className} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
  );
}

export default function TenantSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { tenants, activeTenant, setTenants, setActiveTenant } = useTenantStore();

  // Load tenants via React Query (uses useQuery directly, not useTenantQuery,
  // because we need tenants BEFORE an active tenant is selected)
  const { token } = useAuthStore();
  const { data: fetchedTenants, isLoading } = useQuery<Tenant[]>({
    queryKey: ['tenants'],
    queryFn: () => api.get<Tenant[]>('/tenants'),
    enabled: !!token,
    staleTime: 300000, // 5 minutes - tenant list rarely changes
  });

  // Sync fetched tenants to the store
  useEffect(() => {
    if (fetchedTenants) {
      setTenants(fetchedTenants);
    }
  }, [fetchedTenants, setTenants]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard contract: Escape closes and returns focus to the trigger;
  // ArrowUp/Down rove through menu items with wrap; Home/End jump.
  const closeMenu = useCallback(() => setIsOpen(false), []);
  useMenuKeyboardNav(isOpen, dropdownRef, triggerRef, closeMenu);

  const handleSelect = (tenant: Tenant) => {
    setActiveTenant(tenant);
    setIsOpen(false);
  };

  if (isLoading && tenants.length === 0) {
    return (
      <div className="px-4 py-2">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-8 w-8 rounded bg-gray-700" />
          <div className="h-4 w-24 rounded bg-gray-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-4 py-2" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="flex w-full items-center gap-3 rounded-md bg-gray-800 px-3 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors"
      >
        <BuildingIcon className="h-5 w-5 text-gray-400" />
        <span className="flex-1 truncate">
          {activeTenant?.name || (tenants.length === 0 ? 'No Organization' : 'Select Organization')}
        </span>
        <ChevronIcon className="h-4 w-4 text-gray-400" isOpen={isOpen} />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Switch organization"
          className="absolute left-4 right-4 z-50 mt-1 rounded-md bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          {tenants.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">
              No organizations yet. Create one from the Team page.
            </div>
          ) : (
            <>
              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSelect(tenant)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                >
                  <BuildingIcon className="h-4 w-4 text-theme-tertiary" />
                  <span className="flex-1 truncate text-left">{tenant.name}</span>
                  {activeTenant?.id === tenant.id && (
                    <CheckIcon className="h-4 w-4 text-green-500" />
                  )}
                </button>
              ))}
              {tenants.length > 1 && (
                <div className="border-t border-gray-700 mt-1 pt-1">
                  <p className="px-3 py-1 text-xs text-theme-tertiary">
                    {tenants.length} organization{tenants.length !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
